import { useEffect, useRef, useState } from 'react';
import type { OrdersPageType } from './OrdersPresentation';
import Modal from '@/common/Modal/Modal';
import Spinner from '@/common/Spinner/Spinner';
import useOnlineStatus from '@/hooks/useOnlineStatus';
import { useToast } from '@/context/ToastContext';
import { loadOrderSettings, saveOrderSettings, OrderSettingsDraft } from '@/service/OrderSettingsService';
import { EmailNotifications } from './settings/EmailNotifications';
import { SearchDefaults } from './SearchDefaults';
import type { OrderSearchDefaults, OrderRefreshRate } from '@/library/OrderSettingsHelper';
import styles from './SettingsModal.module.scss';

interface SettingsModalProps {
  currentPage: OrdersPageType;
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars
  onSaved?: (rate: OrderRefreshRate) => void;
}

export function SettingsModal({ currentPage, onClose, onSaved }: SettingsModalProps) {
  const isOnline = useOnlineStatus();
  const { showToast } = useToast();
  const [draft, setDraft] = useState<OrderSettingsDraft | null>(null);
  const [searchDefaults, setSearchDefaults] = useState<OrderSearchDefaults | null>(null);
  const [refreshRate, setRefreshRate] = useState<OrderRefreshRate>(5);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const savingRef = useRef(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const mountedRef = useRef(false);
  const canSave = Boolean(draft && searchDefaults) && !loading && !saving && isOnline;
  const saveLabel = saving ? 'Saving…' : 'Save';

  const closeModal = () => {
    if (!savingRef.current) onClose();
  };

  const handleSave = async () => {
    if (!draft || !searchDefaults || !canSave || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setError('');
    try {
      await saveOrderSettings(draft, selectedIds, searchDefaults, refreshRate);
      if (!mountedRef.current) return;
      onSaved?.(refreshRate);
      showToast({ message: 'Order settings saved.', status: 'success' });
      onClose();
    } catch (saveError) {
      if (mountedRef.current) setError(saveError instanceof Error ? saveError.message : 'Your settings could not be saved. Please retry.');
    } finally {
      savingRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    const previousFocus = document.activeElement;
    dialogRef.current?.focus();
    return () => {
      mountedRef.current = false;
      if (previousFocus instanceof HTMLElement) previousFocus.focus();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      setDraft(null);
      try {
        const settings = await loadOrderSettings(currentPage);
        if (cancelled) return;
        setDraft(settings);
        setRefreshRate(settings.refreshRate ?? 5);
        setSelectedIds(settings.selectedIds);
        setSearchDefaults(settings.searchDefaults);
      } catch (loadError) {
        if (!cancelled) setError(loadError instanceof Error ? loadError.message : 'Settings could not be loaded.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [currentPage]);

  return (
    <Modal isOpen onClose={closeModal} className={styles.modal}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`${currentPage} order settings`}
        aria-busy={loading || saving}
        tabIndex={-1}
        className={styles.content}
        data-testid="orders-settings-modal"
      >
        {loading && (
          <div className={styles.loading} role="status">
            <Spinner />
            <span>Loading settings…</span>
          </div>
        )}
        {draft && searchDefaults && (
          <div className={styles.sections}>
            <SearchDefaults
              refreshRate={refreshRate}
              onRefreshRateChange={setRefreshRate}
              currentPage={currentPage}
              options={draft.filterOptions}
              value={searchDefaults}
              onChange={setSearchDefaults}
              disabled={saving}
            />
            <EmailNotifications options={draft.options} selectedIds={selectedIds} onChange={setSelectedIds} disabled={saving} />
          </div>
        )}
        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
        {!isOnline && <p className={styles.offlineMessage}>Connect to the internet to save your settings.</p>}
        <div className={styles.buttonRow}>
          <button type="button" className={styles.cancelButton} onClick={closeModal} disabled={saving}>
            Cancel
          </button>
          <button type="button" className={styles.saveButton} onClick={() => void handleSave()} disabled={!canSave}>
            {saveLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}
